// Flow Forge Mechanical — analytics wrapper
// Lazy-loads gtag.js when GA4 measurement ID is configured; safe no-op when not.
//
// Env vars (all REACT_APP_* so they're available client-side):
//   REACT_APP_GA4_MEASUREMENT_ID            e.g. "G-XXXXXXXXXX"
//   REACT_APP_GADS_CONVERSION_ID            e.g. "AW-XXXXXXXXX"
//   REACT_APP_GADS_PHONE_CONVERSION_LABEL   e.g. "AbCdEfGh"  (just the label, no slash)
//   REACT_APP_GADS_LEAD_CONVERSION_LABEL    e.g. "JkLmNoPq"

const GA4_ID =
  process.env.REACT_APP_GA_MEASUREMENT_ID ||
  process.env.REACT_APP_GA4_MEASUREMENT_ID ||
  'G-2WYMPF3TY1'; // Flow Forge production fallback — safe to keep in the bundle
const GADS_ID = process.env.REACT_APP_GADS_CONVERSION_ID || '';
const PHONE_LABEL = process.env.REACT_APP_GADS_PHONE_CONVERSION_LABEL || '';
const LEAD_LABEL = process.env.REACT_APP_GADS_LEAD_CONVERSION_LABEL || '';

const UTM_STORAGE_KEY = 'ffm_utm';

let initialized = false;

const log = (...args) => {
  if (typeof window !== 'undefined' && window.__FFM_ANALYTICS_DEBUG__) {
    // Helpful for QA in DebugView setup
    // eslint-disable-next-line no-console
    console.log('[analytics]', ...args);
  }
};

function injectGtag(loaderId, configIds = []) {
  if (typeof window === 'undefined' || document.getElementById('gtag-loader')) return;

  const tag = document.createElement('script');
  tag.id = 'gtag-loader';
  tag.async = true;
  tag.src = `https://www.googletagmanager.com/gtag/js?id=${loaderId}`;
  document.head.appendChild(tag);

  // gtag bootstrap (matches official snippet)
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag('js', new Date());

  // Configure every provided ID. For GA4 we disable auto page_view so we
  // can control it on SPA route change. For Google Ads, the default config
  // (which enables the conversion linker / GCLID handling) is exactly what
  // we want.
  configIds.filter(Boolean).forEach((id) => {
    if (id.startsWith('G-')) {
      gtag('config', id, { send_page_view: false });
    } else {
      gtag('config', id);
    }
  });
}

export function initAnalytics() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  // Allow ?debug_analytics=1 to flip on verbose console logging at runtime.
  // (Independent of env vars — useful for QA on a live build.)
  try {
    const dbg = new URLSearchParams(window.location.search).get('debug_analytics');
    if (dbg === '1' || dbg === 'true') {
      window.__FFM_ANALYTICS_DEBUG__ = true;
      // eslint-disable-next-line no-console
      console.log('[analytics] debug mode ON via ?debug_analytics=1');
    }
  } catch {
    /* ignore */
  }

  // 1. Capture UTM/gclid on first visit and persist for the session
  try {
    const params = new URLSearchParams(window.location.search);
    const collected = {};
    [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
      'gclid',
    ].forEach((k) => {
      const v = params.get(k);
      if (v) collected[k] = v;
    });
    if (Object.keys(collected).length > 0) {
      const existing = JSON.parse(sessionStorage.getItem(UTM_STORAGE_KEY) || '{}');
      // First-touch wins, so only set keys that are not already stored
      const merged = { ...collected, ...existing };
      merged.first_landing_page =
        existing.first_landing_page || window.location.pathname;
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(merged));
      log('UTM captured:', merged);
    } else if (!sessionStorage.getItem(UTM_STORAGE_KEY)) {
      sessionStorage.setItem(
        UTM_STORAGE_KEY,
        JSON.stringify({ first_landing_page: window.location.pathname })
      );
    }
  } catch (e) {
    log('UTM capture failed:', e);
  }

  // 2. Inject gtag if EITHER a GA4 measurement ID or a Google Ads conversion
  // ID is configured. The loader URL uses whichever is present (GA4 preferred).
  const loaderId = GA4_ID || GADS_ID;
  if (loaderId) {
    injectGtag(loaderId, [GA4_ID, GADS_ID]);
    log('gtag initialized — GA4:', GA4_ID || '(none)', 'Ads:', GADS_ID || '(none)');
  } else {
    log('Neither GA4 nor Google Ads ID configured — analytics is a no-op');
  }
}

export function getStoredUTM() {
  try {
    return JSON.parse(sessionStorage.getItem(UTM_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function _track(eventName, params = {}) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    log('skip (no gtag):', eventName, params);
    return;
  }
  const utm = getStoredUTM();
  const merged = {
    ...utm,
    ...params,
    page_location: window.location.href,
    page_path: window.location.pathname,
  };
  window.gtag('event', eventName, merged);
  log('event:', eventName, merged);
}

let _lastPageViewPath = null;

export function trackPageView(path) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  // page_view is a GA4 event — only meaningful when GA4 is configured.
  // (Google Ads doesn't use page_view events for conversion tracking.)
  if (!GA4_ID) return;
  const p = path || window.location.pathname;
  // De-dupe consecutive page_views for the same path — protects against React
  // 18 StrictMode double-invoking useEffect on mount in development. In
  // production a real route change to the same path is rare and this guard
  // has no visible effect on it.
  if (_lastPageViewPath === p) {
    log('page_view suppressed (duplicate):', p);
    return;
  }
  _lastPageViewPath = p;
  window.gtag('event', 'page_view', {
    page_location: window.location.href,
    page_path: p,
    page_title: document.title,
  });
  log('page_view:', p);
}

export function trackClickToCall(phoneHref = '', linkLocation = '') {
  _track('phone_click', { link_location: linkLocation, phone_href: phoneHref });
  if (GADS_ID && PHONE_LABEL) {
    window.gtag('event', 'conversion', {
      send_to: `${GADS_ID}/${PHONE_LABEL}`,
      phone_href: phoneHref,
    });
    log('Ads conversion (phone) fired');
  }
}

export function trackSmsClick(href = '', linkLocation = '') {
  _track('sms_click', { link_location: linkLocation, sms_href: href });
}

export function trackEmailClick(href = '', linkLocation = '') {
  _track('email_click', { link_location: linkLocation, email_href: href });
}

export function trackReviewClick(linkLocation = '') {
  _track('review_link_click', { link_location: linkLocation });
}

// Fired once per successful website lead (service-request or membership signup).
// Uses GA4 recommended event name `generate_lead` so GA4 treats it as a
// conversion by default. Also fires the Google Ads Lead conversion when the
// corresponding env vars are set.
export function trackLeadSubmit(extra = {}) {
  _track('generate_lead', extra);
  if (GADS_ID && LEAD_LABEL) {
    window.gtag('event', 'conversion', {
      send_to: `${GADS_ID}/${LEAD_LABEL}`,
      ...extra,
    });
    log('Ads conversion (lead) fired');
  }
}

// Back-compat alias for older imports — no longer emits the legacy event name.
export const trackServiceRequestSubmit = trackLeadSubmit;

let _thankYouFired = false;
export function trackThankYouView() {
  // React 18 StrictMode double-invokes useEffect on mount in dev. Guard
  // against duplicate fires within a single session.
  if (_thankYouFired) return;
  _thankYouFired = true;
  _track('thank_you_page_view');
}

// GA4-only funnel-analysis event. Fires once per form per session on first
// interaction. NOT a Google Ads conversion.
export function trackFormStart(formKey = 'service_request') {
  if (typeof window === 'undefined') return;
  const sessionFlag = `ffm_form_started_${formKey}`;
  try {
    if (sessionStorage.getItem(sessionFlag)) return;
    sessionStorage.setItem(sessionFlag, '1');
  } catch {
    /* sessionStorage unavailable — fall through */
  }
  _track('form_start', { form_key: formKey });
}

// Back-compat alias
export const trackFormStarted = trackFormStart;

// GA4-only file-attachment tracking. NOT a Google Ads conversion.
export function trackFileUpload(details = {}) {
  _track('file_upload', details);
}

export function isConfigured() {
  return Boolean(GA4_ID);
}
